import { CheckCircle, AlertCircle, Camera } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { DoubleCheckInspection, ReportStyleSettings } from './types';

interface ReportDoubleChecksProps {
  doubleCheckInspections: DoubleCheckInspection[];
  doubleCheckWorkCodes: Array<{ code: string; is_issue: boolean }>;
  styleSettings: ReportStyleSettings;
}

export function ReportDoubleChecks({
  doubleCheckInspections,
  doubleCheckWorkCodes,
  styleSettings,
}: ReportDoubleChecksProps) {
  const { t } = useTranslation('inspection');
  if (doubleCheckInspections.length === 0) return null;

  return (
    <div className="report-doublecheck-section">
      <h2 className="report-section-title" style={{ color: styleSettings.section_heading_color }}>
        {t('report.dcSectionTitle')}
      </h2>
      <table className="report-zone-table">
        <thead>
          <tr style={{ backgroundColor: styleSettings.table_header_color }}>
            <th style={{ color: styleSettings.table_header_label_color }}>{t('report.colDescription')}</th>
            <th style={{ color: styleSettings.table_header_label_color, width: '56px' }}>{t('report.colSize')}</th>
            <th style={{ color: styleSettings.table_header_label_color, width: '64px' }}>{t('report.colStatus')}</th>
            <th style={{ color: styleSettings.table_header_label_color }}>{t('report.colFailureCodesNotes')}</th>
          </tr>
        </thead>
        <tbody>
          {doubleCheckInspections.map((dc) => {
            const hasIssues = dc.failure_codes.length > 0 && dc.failure_codes.some(code => {
              const workCode = doubleCheckWorkCodes.find(wc => wc.code === code);
              return workCode?.is_issue === true;
            });
            const hasPhotos = dc.photo_urls.length > 0;

            return (
              <tr
                key={dc.id}
                className={hasIssues ? 'report-zone-issue-row' : ''}
                style={hasIssues ? { backgroundColor: styleSettings.issue_row_bg_color } : undefined}
              >
                <td className="font-medium" style={{ color: styleSettings.body_text_color }}>{dc.description}</td>
                <td style={{ color: styleSettings.info_label_color }}>{dc.size}"</td>
                <td>
                  {hasIssues ? (
                    <span className="report-status-issue" style={{ color: styleSettings.issue_status_color }}>
                      <AlertCircle size={11} />
                      {t('report.statusIssue')}
                    </span>
                  ) : (
                    <span className="report-status-ok" style={{ color: styleSettings.ok_status_color }}>
                      <CheckCircle size={11} />
                      {t('report.statusOk')}
                    </span>
                  )}
                </td>
                <td>
                  <div>
                    {dc.failure_codes.length > 0 ? (
                      <span style={{ color: hasIssues ? styleSettings.issue_status_color : styleSettings.ok_status_color }}>
                        {dc.failure_codes.join(', ')}
                      </span>
                    ) : (
                      <span style={{ color: styleSettings.ok_status_color }}>{t('report.statusNoIssues')}</span>
                    )}
                    {dc.notes && dc.notes.trim() !== '' && (
                      <span className="text-gray-600 italic ml-1">{/* theme-allow: printed customer report — always light */}({dc.notes})</span>
                    )}
                    {hasPhotos && (
                      <span className="report-photo-ref no-screen">
                        <Camera size={10} className="inline-block" style={{ verticalAlign: 'middle' }} />
                        {' '}{t('report.seePhotoIndex')}
                      </span>
                    )}
                    {hasPhotos && (
                      <span className="report-photo-count no-print">
                        <Camera size={11} className="inline-block" style={{ verticalAlign: 'middle' }} />
                        {' '}{dc.photo_urls.length}
                      </span>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
